// src/stages/stage1Plant/PlantLifeMeter.jsx
import React, { useEffect, useRef, useState } from "react";
import PlantMood from "./PlantMood";
import PlantResourceMeter from "./PlantResourceMeter";
import PlantHUD from "./PlantHUD";

const LEAVES_OPEN_SRC = "/stageAssets/stage1-plant/audio/leavesOpen.mp3";
const LEAVES_CLOSE_SRC = "/stageAssets/stage1-plant/audio/leavesClose.mp3";

export default function PlantLifeMeter({
  setArrowPosition,
  setTargetOpen,
  isStorming = false,
  lightingMode = "day",
  onTriggerEndTransition,
}) {
  const [water, setWater] = useState(2.2);
  const [sun, setSun] = useState(1.0);
  const [nutrients, setNutrients] = useState(0.6);
  const [areLeavesOpen, setAreLeavesOpen] = useState(true);

  const openSfxRef = useRef(null);
  const closeSfxRef = useRef(null);
  const hasTriggeredEndRef = useRef(false);

  const isDay = lightingMode === "day";
  const isTransitionLight =
    lightingMode === "sunset" || lightingMode === "sunrise";

  // preload one-shot leaf sounds
  useEffect(() => {
    openSfxRef.current = new Audio(LEAVES_OPEN_SRC);
    closeSfxRef.current = new Audio(LEAVES_CLOSE_SRC);

    openSfxRef.current.preload = "auto";
    closeSfxRef.current.preload = "auto";

    openSfxRef.current.volume = 0.5;
    closeSfxRef.current.volume = 0.5;

    return () => {
      if (openSfxRef.current) {
        openSfxRef.current.pause();
        openSfxRef.current = null;
      }

      if (closeSfxRef.current) {
        closeSfxRef.current.pause();
        closeSfxRef.current = null;
      }
    };
  }, []);

  const playOpenSfx = () => {
    const audio = openSfxRef.current;
    if (!audio) return;

    try {
      audio.currentTime = 0;
      audio.play().catch((err) => {
        console.warn("Open leaves audio blocked:", err);
      });
    } catch (err) {
      console.warn("Open leaves audio error:", err);
    }
  };

  const playCloseSfx = () => {
    const audio = closeSfxRef.current;
    if (!audio) return;

    try {
      audio.currentTime = 0;
      audio.play().catch((err) => {
        console.warn("Close leaves audio blocked:", err);
      });
    } catch (err) {
      console.warn("Close leaves audio error:", err);
    }
  };

  // open/close leaves
  const handleOpenLeaves = () => {
    if (areLeavesOpen) return;

    setAreLeavesOpen(true);
    setTargetOpen?.(1);
    playOpenSfx();
  };

  const handleCloseLeaves = () => {
    if (!areLeavesOpen) return;

    setAreLeavesOpen(false);
    setTargetOpen?.(0);
    playCloseSfx();
  };

  // trigger end Transition based on nutrients
  useEffect(() => {
    if (hasTriggeredEndRef.current) return;

    const isDay = lightingMode === "day";

    if (nutrients >= 2.6 && isDay) {
      hasTriggeredEndRef.current = true;
      onTriggerEndTransition?.();
    }
  }, [nutrients, lightingMode, onTriggerEndTransition]);

  // core plant/life meter game-play
  useEffect(() => {
    const interval = setInterval(() => {
      // WATER
      setWater((prev) => {
        let next = prev;

        if (isStorming && areLeavesOpen) {
          next += 0.2;
        }

        if (isDay && areLeavesOpen && !isStorming) {
          next -= 0.06;
        }

        // natural stabilization
        if (!isStorming) {
          if (next < 2.5) next += 0.03;
          if (next > 2.5) next -= 0.01;
        }

        return Math.max(0, Math.min(5, next));
      });

      // SUN
      setSun((prev) => {
        let next = prev;

        if (areLeavesOpen && isDay) {
          next += 0.12;
        } else if (areLeavesOpen && isTransitionLight) {
          next += 0.06; // half-sun during sunrise/sunset
        } else {
          next -= 0.04;
        }

        return Math.max(0, Math.min(5, next));
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [isStorming, areLeavesOpen, isDay, isTransitionLight]);

  // nutrients rise only when both water and sun stay balanced
  useEffect(() => {
    const interval = setInterval(() => {
      setNutrients((prev) => {
        const waterHealthy = water >= 2 && water <= 4;
        const sunHealthy = sun >= 2 && sun <= 4;

        if (waterHealthy && sunHealthy) {
          return Math.min(5, prev + 0.1);
        }

        return Math.max(0, prev - 0.025);
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [water, sun]);

  return (
    <>
      <div className="plant-life-meter-container">
        <PlantResourceMeter
          label="Water"
          icon="💧"
          value={water}
          total={5}
          accentClass="water-meter"
        />

        <PlantResourceMeter
          label="Sun"
          icon="☀️"
          value={sun}
          total={5}
          accentClass="sun-meter"
        />

        <PlantResourceMeter
          label="Nutrients"
          icon="🌱"
          value={nutrients}
          total={5}
          accentClass="nutrient-meter"
        />

        <PlantMood setArrowPosition={setArrowPosition} />
      </div>

      <PlantHUD
        onOpenLeaves={handleOpenLeaves}
        onCloseLeaves={handleCloseLeaves}
      />
    </>
  );
}